import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

// Optimized translation resources - structured for performance
const resources = {
  en: {
    about: {
      hero: {
        title: 'About CLEAR',
        subtitle: 'CLEAR aims to strengthen NRC\'s capacity to prepare and respond to humanitarian crises through predictive analytics, enhanced situational awareness and streamlined information with the core mission to better serve people on the move.',
        nrcExperience: '75 years of humanitarian expertise driving this innovation'
      },
      problem: {
        title: 'Critical Technical Limitations in Humanitarian Response',
        subtitle: 'The humanitarian sector faces critical technical limitations that prevent effective crisis response',
        areas: {
          dataVelocity: {
            title: 'Data Velocity Gap',
            description: 'Organizations process crisis indicators manually across 50+ disconnected sources. Current mean-time-to-detection averages 72-96 hours when industry-standard streaming architectures could reduce this to under 1 hour.'
          },
          fragmentation: {
            title: 'Intelligence Fragmentation',
            description: 'Field teams navigate 15-20 separate systems during crisis response, losing 4-6 hours daily to manual data correlation. Without unified data lakes and real-time ETL pipelines, critical patterns remain invisible.'
          },
          predictive: {
            title: 'Predictive Blind Spots',
            description: 'Despite possessing 10+ years of historical crisis data, organizations cannot forecast population movements or resource needs. Machine learning models offer significant accuracy improvements but require sophisticated MLOps infrastructure.'
          },
          scale: {
            title: 'Scale Constraints',
            description: 'Existing systems fail at crisis-level load. Cloud-native architectures with auto-scaling could handle massive traffic spikes, but implementation requires specialized expertise that humanitarian organizations typically lack.'
          }
        },
        impact: {
          title: 'Global Impact Statistics',
          subtitle: 'Data demonstrates the scope and urgency of these technical limitations'
        }
      },
      vision: {
        title: 'Our Vision',
        subtitle: 'CLEAR aims to strengthen NRC\'s capacity to prepare and respond to humanitarian crises through predictive analytics, enhanced situational awareness and streamlined information with the core mission to better serve people on the move.',
        items: {
          hoursMatter: {
            title: 'Risk, Alert, and Situation Awareness',
            description: 'By enabling earlier risk detection (RISK), timely early warning (ALERT), and situation awareness (SITREP), CLEAR will make emergency response better informed, faster, and where possible, anticipatory.'
          },
          anticipation: {
            title: 'Community-Centered Response',
            description: 'CLEAR will transform how we understand and respond to crises by centering community voices at each step of the response, capturing their perspectives wherever feasible, and empowering (local) emergency responders with timely access to the right information, and solutions at critical moments.'
          },
          unifiedIntel: {
            title: 'AI-Powered Intelligence',
            description: 'Leveraging modern data infrastructure, predictive analytics, and AI, CLEAR will extract insights, synthesize information, and communicate effectively, turning raw data into actionable intelligence enabling timely action and further bringing focus on the much needed service delivery.'
          },
          globalScale: {
            title: 'Breaking Silos, Building Collaboration',
            description: 'CLEAR represents an opportunity to consolidate efforts, break organizational silos, and bring diverse stakeholders from different sectors together around a shared goal to bring AI to the service of humanity.'
          }
        }
      },
      mission: {
        title: 'Responsible AI for Humanitarian Action',
        statement: 'Humanitarians operate in challenging contexts often characterised by limited data availability and connectivity. While holding immense potential in supporting humanitarian action, AI and emerging technologies are tools, not silver bullets. We remain committed to responsible AI use, maintaining alignment with humanitarian principles and ensuring continuous human evaluation and oversight. AI cannot replace human judgment, local knowledge, or the fundamental work of humanitarian responders and the sector should remain humble about the technology\'s limitations and committed to being transparent about where it can and cannot help.'
      },
      dataParadox: {
        title: 'The Data Paradox',
        subtitle: 'Humanitarian organizations operate in a data paradox: drowning in information yet blind to emerging crises. Current systems fail at four critical junctures.',
        junctures: {
          velocity: {
            title: 'Data Velocity Gap',
            description: 'Organizations process crisis indicators manually across 50+ disconnected sources. Current mean-time-to-detection averages 72-96 hours when industry-standard streaming architectures could reduce this to under 1 hour.'
          },
          fragmentation: {
            title: 'Intelligence Fragmentation',
            description: 'Field teams navigate 15-20 separate systems during crisis response, losing 4-6 hours daily to manual data correlation. Without unified data lakes and real-time ETL pipelines, critical patterns remain invisible.'
          },
          predictive: {
            title: 'Predictive Paralysis',
            description: 'Despite possessing decades of crisis data, organizations cannot forecast population movements or resource needs. The sector lacks MLOps infrastructure to operationalize predictive models at scale.'
          },
          scale: {
            title: 'Scale Constraints',
            description: 'Existing systems fail at crisis-level load. Cloud-native architectures with auto-scaling could handle massive traffic spikes, but implementation requires specialized expertise humanitarian organizations lack.'
          }
        },
        stakeholders: {
          title: 'Key Stakeholders',
          primary: {
            title: 'Primary',
            description: 'Crisis-affected populations requiring timely assistance'
          },
          operational: {
            title: 'Operational',
            description: 'NRC field teams, emergency responders, program managers'
          },
          strategic: {
            title: 'Strategic',
            description: 'Partner organizations, UN clusters, donors, local governments'
          }
        },
        opportunities: {
          title: 'New Opportunities',
          description: 'Modern cloud architectures now enable real-time, predictive humanitarian intelligence at unprecedented scale. By combining streaming data pipelines, unified analytics, and LLM-powered reasoning, we can transform crisis response from reactive firefighting to proactive intervention.'
        }
      },
      cascadingCrises: {
        title: 'The Cascading Effects of Compounding Crises',
        subtitle: 'Modern humanitarian crises rarely occur in isolation. Instead, they create cascading effects where one emergency triggers or amplifies others, creating compound disasters that overwhelm response capacity.',
        patterns: {
          climate: {
            title: 'Climate → Displacement → Conflict',
            description: 'In East Africa, heavy rains flooded refugee camps, forcing secondary displacement. Competition over depleted resources then sparked community conflicts. Three-quarters of forcibly displaced people live in countries heavily impacted by climate change.'
          },
          conflict: {
            title: 'Conflict → Economic Crisis → Food Insecurity',
            description: 'Sudan\'s conflict displaced 14.3 million people, disrupting agricultural production. Yemen has 4.8 million IDPs with 18 million dependent on humanitarian assistance. Severe acute malnutrition increased 34% from 2023.'
          },
          displacement: {
            title: 'Displacement → Infrastructure Collapse → Health Crisis',
            description: 'Ukraine faces renewed attacks on energy infrastructure disrupting heating and water for millions. Gaza\'s infrastructure destruction left communities without basic services while displacement continued.'
          }
        },
        compoundRisk: {
          title: 'Compound Risk Reality',
          subtitle: 'Traditional single-hazard risk assessments fail to capture these compound effects, leaving humanitarian responses unprepared for cascading failures.'
        }
      },
      cta: {
        title: 'Join the Transformation',
        subtitle: 'Be part of the shift from reactive to anticipatory humanitarian response',
        button: 'Get Early Access'
      }
    },
    landing: {
      hero: {
        title: 'CLEAR',
        subtitle: 'Crisis Learning, Early-warning, Anticipation, and Response',
        description: 'A comprehensive digital ecosystem boosted by principled AI to guide humanitarian decision-makers and emergency teams.',
        badge: 'Early Warning • Early Action',
        cta: {
          primary: 'Get Notified at Launch',
          secondary: 'Watch Demo',
          earlyAccess: 'Get Early Access',
          learnMore: 'Learn More'
        }
      },
      capabilities: {
        title: 'Four Integrated Components',
        subtitle: 'Unified intelligence for rapid crisis response',
        foundation: {
          title: 'EWEA Data Mesh',
          description: 'ML models trained on 10+ years of crisis data forecast population movements and resource needs'
        },
        components: {
          detection: {
            title: 'Crisis Detection and Alert Engine',
            description: 'Real-time monitoring across satellite imagery, weather patterns, social media, news feeds, and field reports. Machine learning trained on a decade of crisis data identify high-risk patterns, delivering smart alerts to decision-makers via web, email, or mobile app based on severity and geographic impact.'
          },
          situationAnalysis: {
            title: 'Situation Analysis Generator',
            description: 'Automated synthesis of hundreds of data sources into actionable intelligence. Using large language models, the AI process information in multiple languages, identifying trends and patterns and generating comprehensive situation reports within hours of an event occurring.'
          },
          fieldInterface: {
            title: 'Field Response Console',
            description: 'Mobile-first dashboard delivering operational intelligence directly to field teams. A natural language interface allows users to ask questions to the data and receive instant, contextual answers, even in low-bandwidth environments.'
          },
          cashDistribution: {
            title: 'Rapid Cash Distribution Engine',
            description: 'Predictive vulnerability modeling that analyses household composition, asset loss, displacement status, and protection risks to enable faster, more accurate targeting of emergency cash assistance, including community validation mechanisms.'
          }
        }
      },
      demo: {
        title: 'See CLEAR in Action',
        subtitle: 'Interactive demonstration of real system capabilities',
        dashboard: 'Crisis Dashboard',
        crisisView: 'Crisis Response Interface',
        stats: 'Live Impact Metrics'
      },
      stats: {
        title: 'Global Humanitarian Impact',
        metrics: {
          crises: {
            value: '2.4M',
            label: 'People Reached'
          },
          response: {
            value: '48hr',
            label: 'Average Response Time'
          },
          accuracy: {
            value: '94%',
            label: 'Prediction Accuracy'
          },
          organizations: {
            value: '150+',
            label: 'Partner Organizations'
          }
        }
      },
      ourVision: {
        title: 'Our Vision',
        subtitle: 'Strengthen crisis response through predictive analytics, situational awareness, and streamlined intelligence—better serving people on the move.',
        items: {
          riskAlert: {
            title: 'Risk, Alert & Situation Awareness',
            description: 'Earlier detection, timely warning, and situational awareness make emergency response faster, smarter, and anticipatory.'
          },
          communityCentered: {
            title: 'Community-Centered Response',
            description: 'Centering community voices and empowering local responders with timely access to the right information.'
          },
          aiPowered: {
            title: 'AI-Powered Intelligence',
            description: 'Modern infrastructure and predictive AI turn raw data into actionable intelligence for focused service delivery.'
          },
          collaboration: {
            title: 'Breaking Silos, Building Together',
            description: 'Consolidating efforts and bringing diverse stakeholders together to bring AI to the service of humanity.'
          }
        },
        responsible: {
          title: 'Responsible AI Use',
          description: 'AI is a tool, not a silver bullet. We maintain alignment with humanitarian principles and ensure continuous human oversight.'
        }
      },
      clearInitiative: {
        paragraph1: 'Building the connective tissue to empower humanitarian organizations to respond faster to emerging crises',
        paragraph2: ''
      },
      humanitarianChallenge: {
        title: 'The Humanitarian Challenge',
        subtitle: 'Four critical gaps slowing crisis response',
        challenges: {
          intelligenceGap: {
            title: 'Intelligence-to-Action Gap',
            description: 'Siloed warnings don\'t trigger action. Teams need integrated intelligence, not disconnected signals.'
          },
          fragmentation: {
            title: 'System Fragmentation',
            description: 'Multiple incompatible systems force reliance on outdated reports and instinct over real-time data.'
          },
          latency: {
            title: 'Detection Delays',
            description: 'Insights arrive too late. Processing delays mean responses trail behind fast-moving crises.'
          },
          technologyLag: {
            title: 'Technology Lag',
            description: 'Proven solutions exist, but adoption lags years behind. Better tools remain underutilized due to training gaps and organizational inertia.'
          }
        }
      },
      vision: {
        title: 'Foundational Principles',
        subtitle: 'CLEAR transforms how humanitarian organizations detect, assess, and respond to crises. By combining AI-powered early warning with rapid response capabilities, we\'re building the infrastructure for anticipatory humanitarian action.',
        items: {
          hoursMatter: {
            title: 'Early action saves lives',
            description: 'Humanitarian response is a race against time. CLEAR transforms how NRC and its partners operate by providing real-time situation awareness and enabling anticipatory action.'
          },
          dataDriven: {
            title: 'Quality Data for Meaningful Decisions',
            description: 'Replace intuition with intelligence. CLEAR builds data foundations necessary to unlock AI\'s potential for humanitarian operations.'
          },
          fieldCentered: {
            title: 'Technology independence through open source',
            description: 'Open-source solutions grounded on humanitarian principles. CLEAR keeps technology open so organizations stay in control, and able to adapt tools to their reality.'
          },
          rapidDeployment: {
            title: 'User/Human-Centered Design',
            description: 'Intuitive interfaces for complex situations. CLEAR is designed by and for emergency responders, from local groups to larger humanitarian institutions, working in challenging environments.'
          }
        },
        comparison: {
          title: 'Faster, Smarter Humanitarian Response',
          subtitle: 'CLEAR doesn\'t prevent crises, but it dramatically reduces response time and removes guesswork from humanitarian decision-making, getting life-saving support to those who need it faster.',
          traditional: {
            title: 'Traditional Approach',
            steps: {
              step1: 'Crisis Occurs',
              step2: 'Wait for Reports',
              step3: 'Manual Assessment',
              step4: 'Slow Decision',
              step5: 'Delayed Response'
            },
            timing: {
              day1: '',
              day2: '',
              day3: '',
              day4: '',
              day5: ''
            },
            responseTime: 'Response Time: Up to 4 Weeks'
          },
          withClear: {
            title: 'With CLEAR',
            steps: {
              step1: 'Auto Detection',
              step2: 'AI Analysis',
              step3: 'Smart Assessment',
              step4: 'Data-Driven Decision',
              step5: 'Rapid Response'
            },
            timing: {
              hour1: '',
              hour2: '',
              hour3: '',
              hour4: '',
              hour5: ''
            },
            responseTime: 'Response Time: 72 Hours'
          },
          improvements: {
            faster: 'Faster Response',
            lessGuesswork: 'Less Guesswork',
            moreData: 'More Data Points'
          }
        }
      },
      whyPartner: {
        title: 'Why Partner With CLEAR?',
        subtitle: 'A purpose-built platform where data sources and technologies converge to drive humanitarian impact—integrating contributions into operational workflows that inform life-saving decisions.',
        benefits: {
          amplify: {
            title: 'Amplifies Impact',
            description: 'Your data feeds directly into early warning systems and field operations, delivering insights when decisions are made.'
          },
          demonstrate: {
            title: 'Demonstrates Value',
            description: 'Clear metrics show how contributions improve response speed and targeting accuracy with transparent impact tracking.'
          },
          trust: {
            title: 'Builds Trust',
            description: 'Ground-truth validation and field feedback ensure your data is reliable and actionable for decision-makers.'
          },
          scale: {
            title: 'Reaches Scale',
            description: 'Contributions shared across NRC\'s 40-country footprint and humanitarian consortium, multiplying social impact.'
          }
        }
      },
      earlyAccess: {
        title: 'Be Among the First When CLEAR Goes Open Source',
        subtitle: 'CLEAR will be released as open source software. Join humanitarian organizations preparing for this revolutionary early warning and response platform.',
        form: {
          title: 'Get Notified at Launch',
          subtitle: 'Be notified when CLEAR becomes available as open source',
          email: {
            label: 'Email Address',
            placeholder: 'your.email@organization.org'
          },
          organization: {
            label: 'Organization',
            placeholder: 'UN Agency, NGO, Government...'
          },
          role: {
            label: 'Role',
            placeholder: 'Select your role',
            options: {
              countryDirector: 'Country Director',
              programManager: 'Program Manager',
              fieldCoordinator: 'Field Coordinator',
              dataAnalyst: 'Data Analyst',
              emergencyCoordinator: 'Emergency Coordinator',
              researcher: 'Researcher',
              other: 'Other'
            }
          },
          submit: 'Get Notified at Launch',
          submitting: 'Signing Up...',
          privacy: 'We\'ll only contact you about the CLEAR open source launch. No spam, unsubscribe anytime.',
          success: {
            title: 'You\'re on the list!',
            message: 'Thank you! You\'ll be among the first to know when CLEAR goes open source.'
          },
          error: 'Something went wrong. Please try again.'
        }
      },
      cta: {
        title: 'Be Among the First When CLEAR Goes Open Source',
        subtitle: 'Join humanitarian organizations preparing for the open source release of this revolutionary crisis response platform',
        button: 'Get Notified at Launch'
      },
      navigation: {
        login: 'Login',
        signup: 'Sign Up',
        language: 'Language'
      },
      footer: {
        description: 'Crisis Learning, Early-warning, Anticipation, and Response platform for humanitarian operations.',
        status: 'In Development',
        organization: 'Norwegian Refugee Council',
        contact: {
          title: 'Contact',
          switchboard: 'Switchboard (09-15 CET)',
          media: 'Media hotline'
        },
        donor: {
          title: 'Donor support',
          hours: '(10.00-12.00/ 12.30-14.00 CET)',
          phone: 'Phone'
        },
        impact: 'Over 90 per cent of our global revenue goes directly to fulfil our purpose.',
        links: {
          privacy: 'Privacy policy',
          cookies: 'About cookies',
          nrc: 'Norwegian Refugee Council'
        }
      },
      signup: {
        title: 'Get involved with the CLEAR project',
        subtitle: 'Be notified when CLEAR becomes available as open source',
        placeholder: 'Enter your email address',
        button: 'Get Updates',
        submitting: 'Submitting...',
        success: {
          title: 'Thank you!',
          message: 'You\'ll be notified when CLEAR launches.'
        },
        error: 'Something went wrong. Please try again.'
      }
    }
  },
  no: {
    landing: {
      hero: {
        title: 'Transformer humanitær respons med tidlig varsling',
        subtitle: 'CLEAR styrker feltteam med AI-drevet krisedeteksjon og proaktive responsegenskaper',
        description: 'Crisis Learning, Early-warning, Anticipation, and Response - Et omfattende økosystem for humanitær beslutningsstøtte',
        badge: 'Tidlig varsling • Tidlig handling',
        cta: {
          primary: 'Bli varslet ved lansering',
          secondary: 'Se demo',
          earlyAccess: 'Få tidlig tilgang',
          learnMore: 'Lær mer'
        }
      },
      capabilities: {
        title: 'Fire integrerte komponenter',
        subtitle: 'Bygget på et enhetlig datafundament som muliggjør tidlig respons for å redde liv',
        foundation: {
          title: 'EWEA Data Mesh',
          description: 'Grunnleggende datalag som gir prediktiv analyse ved å utnytte ML-modeller trent på 10+ år med NRC-krisedata for å forutsi befolkningsbevegelser, ressursbehov og kriseutviklingsmønstre'
        },
        components: {
          detection: {
            title: 'Krisedeteksjon og varselsmotor',
            description: 'Sanntidsovervåking på tvers av satellittbilder, værmønstre, sosiale medier, nyhetsfeeder og feltrapporter. Maskinlæring trent på et tiår med krisedata identifiserer høyrisikomønstre, og leverer smarte varsler til beslutningstakere via nett, e-post eller mobilapp basert på alvorlighetsgrad og geografisk påvirkning.'
          },
          situationAnalysis: {
            title: 'Situasjonsanalysegenerator',
            description: 'Automatisert syntese av hundrevis av datakilder til handlingsrettet intelligens. Ved hjelp av store språkmodeller behandler AI informasjon på flere språk, identifiserer trender og mønstre og genererer omfattende situasjonsrapporter innen timer etter en hendelse.'
          },
          fieldInterface: {
            title: 'Feltresponskonsoll',
            description: 'Mobilfokusert dashbord som leverer operasjonell intelligens direkte til feltteam. Et naturlig språkgrensesnitt lar brukere stille spørsmål til dataene og motta umiddelbare, kontekstuelle svar, selv i lavbåndbreddemiljøer.'
          },
          cashDistribution: {
            title: 'Rask kontantdistribusjonsmotor',
            description: 'Prediktiv sårbarhetsmodellering som analyserer husholdningssammensetning, tap av eiendeler, forflytningsstatus og beskyttelsesrisiko for å muliggjøre raskere, mer nøyaktig målretting av nødhjelp i form av kontanter, inkludert samfunnsvalideringsmekanismer.'
          }
        }
      },
      demo: {
        title: 'Se CLEAR i aksjon',
        subtitle: 'Interaktiv demonstrasjon av faktiske systemkapasiteter',
        dashboard: 'Krisedashboard',
        crisisView: 'Kriseresponsgrensesnitt',
        stats: 'Live påvirkningsmetrikker'
      },
      stats: {
        title: 'Global humanitær påvirkning',
        metrics: {
          crises: {
            value: '2,4M',
            label: 'Personer nådd'
          },
          response: {
            value: '48t',
            label: 'Gjennomsnittlig responstid'
          },
          accuracy: {
            value: '94%',
            label: 'Prediksjonsgrunnlighet'
          },
          organizations: {
            value: '150+',
            label: 'Partnerorganisasjoner'
          }
        }
      },
      ourVision: {
        title: 'Vår visjon',
        subtitle: 'CLEAR har som mål å styrke NRCs kapasitet til å forberede og respondere på humanitære kriser gjennom prediktiv analyse, forbedret situasjonsbevissthet og strømlinjeformet informasjon—for bedre å tjene mennesker på flyttefot.',
        items: {
          riskAlert: {
            title: 'Risiko, varsling og situasjonsbevissthet',
            description: 'Muliggjør tidligere risikodeteksjon, rettidig tidlig varsling og situasjonsbevissthet for å gjøre nødrespons bedre informert, raskere og der det er mulig, førebyggende.'
          },
          communityCentered: {
            title: 'Samfunnssentrert respons',
            description: 'Sentrer lokalsamfunnets stemmer i hvert steg, fanger perspektivene deres, og styrker lokale nødhjelpsarbeidere med rettidig tilgang til riktig informasjon.'
          },
          aiPowered: {
            title: 'AI-drevet intelligens',
            description: 'Gjør rådata om til handlingsrettet intelligens gjennom moderne datainfrastruktur, prediktiv analyse og AI—muliggjør rettidig handling og fokusert tjenesteleveranse.'
          },
          collaboration: {
            title: 'Bryte siloer, bygge sammen',
            description: 'Konsolidere innsats, bryte organisatoriske siloer, og bringe diverse interessenter sammen rundt et felles mål om å bringe AI i menneskehetens tjeneste.'
          }
        },
        responsible: {
          title: 'Ansvarlig AI-bruk',
          description: 'AI og nye teknologier er verktøy, ikke mirakelmidler. Vi forblir forpliktet til ansvarlig AI-bruk, opprettholder tilpasning til humanitære prinsipper, sikrer kontinuerlig menneskelig tilsyn, og forblir ydmyke om teknologiens begrensninger.'
        }
      },
      cta: {
        title: 'Vær blant de første når CLEAR blir åpen kildekode',
        subtitle: 'Bli med humanitære organisasjoner som forbereder seg på åpen kildekode-utgivelsen av denne revolusjonerende kriseresponsplattformen',
        button: 'Bli varslet ved lansering'
      },
      humanitarianChallenge: {
        title: 'Den humanitære utfordringen',
        subtitle: 'Nåværende humanitære responssystemer møter fire kritiske begrensninger som CLEAR direkte adresserer',
        challenges: {
          intelligenceGap: {
            title: 'Gapet mellom intelligens og handling',
            description: 'Eksisterende tidlig varslingsverktøy tilbyr isolerte innsikter (konflikt, klima, fordrivelse) men produserer ikke operasjonelle utløsere. Beslutningstakere i felten trenger integrert, kontekstualisert intelligens, ikke rå signaler.'
          },
          fragmentation: {
            title: 'Systemfragmentering',
            description: 'Feltteam må navigere flere, ikke-interoperable systemer som ikke er designet for komplekse, multi-risiko kriser. De er avhengige av utdaterte rapporter, ad hoc-koordinering eller instinkt. Sanntidsbeslutninger krever enhetlige, felttilpassede verktøy.'
          },
          latency: {
            title: 'Forsinkelse i deteksjon og kommunikasjon',
            description: 'Selv de beste innsiktene kommer for sent. Forsinkelser i behandling, kommunikasjon og koordinering betyr at responser ligger bak raskt utviklende kriser, spesielt de med kaskadeeffekter.'
          },
          principledAI: {
            title: 'Det vanskelige spørsmålet om prinsippbasert bruk av AI',
            description: 'Selv om AI har enormt potensial for humanitær handling, må vi navigere komplekse etiske hensyn rundt skjevhet, ansvarlighet og menneskelig tilsyn for å sikre at teknologi tjener humanitære prinsipper.'
          }
        }
      },
      vision: {
        title: 'Hvordan CLEAR adresserer disse utfordringene',
        subtitle: 'CLEAR transformerer fragmenterte signaler til enhetlig, handlingsrettet intelligens—som gjør feltteam i stand til å respondere raskere og mer effektivt for å redde liv.',
        items: {
          hoursMatter: {
            title: 'Handlingsrettet intelligens',
            description: 'Transformerer isolerte tidlig varsling signaler til operasjonelle utløsere. Feltbesluttakere mottar integrert, kontekstualisert intelligens—ikke rådata—som muliggjør umiddelbar handling når kriser oppstår.'
          },
          dataDriven: {
            title: 'Enhetlig kommandosenter',
            description: 'Konsoliderer 15+ frakoblede systemer til en enkelt sanntidsplattform. Eliminerer timer med manuell datakorrelasjon, og avslører kritiske mønstre umiddelbart gjennom AI-drevet syntese.'
          },
          fieldCentered: {
            title: 'Felttilpasset grensesnitt',
            description: 'Spesialbygget for humanitære arbeidere i lavkonnektivitets-, høystressmiljøer. Intuitive verktøy designet for komplekse multi-risiko kriser—erstatter ad hoc-koordinering med systematisk respons.'
          },
          rapidDeployment: {
            title: 'Null-latens respons',
            description: 'Behandler og kommuniserer intelligens i sanntid. Fra deteksjon til utplassering på timer i stedet for dager—holder tritt med raskt utviklende kriser og kaskadeeffekter gjennom automatiserte arbeidsflyter.'
          }
        }
      },
      earlyAccess: {
        title: 'Vær blant de første når CLEAR blir åpen kildekode',
        subtitle: 'CLEAR vil bli utgitt som åpen kildekode-programvare. Bli med humanitære organisasjoner som forbereder seg på denne revolusjonerende tidlige varslings- og responsplattformen.',
        form: {
          title: 'Bli varslet ved lansering',
          subtitle: 'Bli varslet når CLEAR blir tilgjengelig som åpen kildekode',
          email: {
            label: 'E-postadresse',
            placeholder: 'din.epost@organisasjon.no'
          },
          organization: {
            label: 'Organisasjon',
            placeholder: 'FN-byrå, NGO, Regjering...'
          },
          role: {
            label: 'Rolle',
            placeholder: 'Velg din rolle',
            options: {
              countryDirector: 'Landsdirektør',
              programManager: 'Programleder',
              fieldCoordinator: 'Feltkoordinator',
              dataAnalyst: 'Dataanalytiker',
              emergencyCoordinator: 'Beredskapskoordinator',
              researcher: 'Forsker',
              other: 'Annet'
            }
          },
          submit: 'Bli varslet ved lansering',
          submitting: 'Registrerer...',
          privacy: 'Vi kontakter deg kun om CLEAR åpen kildekode-lansering. Ingen spam, avmeld når som helst.',
          success: {
            title: 'Du er på listen!',
            message: 'Takk! Du vil være blant de første til å vite når CLEAR blir åpen kildekode.'
          },
          error: 'Noe gikk galt. Prøv igjen.'
        }
      },
      navigation: {
        login: 'Logg inn',
        signup: 'Registrer deg',
        language: 'Språk'
      },
      footer: {
        description: 'Crisis Learning, Early-warning, Anticipation, and Response plattform for humanitære operasjoner.',
        status: 'Under utvikling',
        organization: 'Flyktninghjelpen',
        contact: {
          title: 'Kontakt',
          switchboard: 'Sentralbord (09-15 CET)',
          media: 'Mediehotline'
        },
        donor: {
          title: 'Giverservice',
          hours: '(10.00-12.00/ 12.30-14.00 CET)',
          phone: 'Telefon'
        },
        impact: 'Over 90 prosent av våre globale inntekter går direkte til å oppfylle vårt formål.',
        links: {
          privacy: 'Personvernpolicy',
          cookies: 'Om informasjonskapsler',
          nrc: 'Flyktninghjelpen'
        }
      },
      signup: {
        title: 'Bli involvert i CLEAR-prosjektet',
        subtitle: 'Bli varslet når CLEAR blir tilgjengelig som åpen kildekode',
        placeholder: 'Skriv inn din e-postadresse',
        button: 'Få oppdateringer',
        submitting: 'Sender inn...',
        success: {
          title: 'Takk!',
          message: 'Du vil bli varslet når CLEAR lanseres.'
        },
        error: 'Noe gikk galt. Prøv igjen.'
      }
    }
  },
  de: {
    landing: {
      hero: {
        title: 'Transformieren Sie die humanitäre Reaktion mit Frühwarnung',
        subtitle: 'CLEAR stärkt Feldteams mit KI-gestützter Krisenerkennung und proaktiven Reaktionsfähigkeiten',
        description: 'Crisis Learning, Early-warning, Anticipation, and Response - Ein umfassendes Ökosystem für humanitäre Entscheidungsunterstützung',
        badge: 'Frühwarnung • Frühes Handeln',
        cta: {
          primary: 'Bei Start benachrichtigen',
          secondary: 'Demo ansehen',
          earlyAccess: 'Frühen Zugang erhalten',
          learnMore: 'Mehr erfahren'
        }
      },
      capabilities: {
        title: 'Vier integrierte Komponenten',
        subtitle: 'Aufgebaut auf einem einheitlichen Datenfundament, das frühe Reaktionen ermöglicht, um Leben zu retten',
        foundation: {
          title: 'EWEA Data Mesh',
          description: 'Grundlegende Datenschicht, die prädiktive Analysen durch ML-Modelle ermöglicht, die mit über 10 Jahren NRC-Krisendaten trainiert wurden, um Bevölkerungsbewegungen, Ressourcenbedarf und Krisenentwicklungsmuster vorherzusagen'
        },
        components: {
          detection: {
            title: 'Krisenerkennung- und Warnmotor',
            description: 'Echtzeitüberwachung von Satellitenbildern, Wettermustern, sozialen Medien, Nachrichtenfeeds und Feldberichten. Mit einem Jahrzehnt an Krisendaten trainiertes maschinelles Lernen identifiziert Hochrisikomuster und liefert intelligente Warnungen an Entscheidungsträger per Web, E-Mail oder mobiler App basierend auf Schweregrad und geografischer Auswirkung.'
          },
          situationAnalysis: {
            title: 'Situationsanalysegenerator',
            description: 'Automatisierte Synthese von Hunderten von Datenquellen zu handlungsorientierter Intelligenz. Mithilfe großer Sprachmodelle verarbeitet die KI Informationen in mehreren Sprachen, identifiziert Trends und Muster und erstellt innerhalb von Stunden nach einem Ereignis umfassende Situationsberichte.'
          },
          fieldInterface: {
            title: 'Feldreaktionskonsole',
            description: 'Mobile-First-Dashboard, das operative Intelligenz direkt an Feldteams liefert. Eine natürlichsprachliche Schnittstelle ermöglicht es Benutzern, Fragen an die Daten zu stellen und sofortige, kontextuelle Antworten zu erhalten, selbst in Umgebungen mit geringer Bandbreite.'
          },
          cashDistribution: {
            title: 'Schneller Bargeldverteilungsmotor',
            description: 'Prädiktive Verwundbarkeitmodellierung, die Haushaltszusammensetzung, Vermögensverlust, Vertreibungsstatus und Schutzrisiken analysiert, um eine schnellere, genauere Ausrichtung von Notfallbargeldunterstützung zu ermöglichen, einschließlich gemeinschaftlicher Validierungsmechanismen.'
          }
        }
      },
      demo: {
        title: 'CLEAR in Aktion sehen',
        subtitle: 'Interaktive Demonstration der tatsächlichen Systemfähigkeiten',
        dashboard: 'Krisen-Dashboard',
        crisisView: 'Krisen-Reaktions-Interface',
        stats: 'Live-Impact-Metriken'
      },
      stats: {
        title: 'Globale humanitäre Auswirkung',
        metrics: {
          crises: {
            value: '2,4M',
            label: 'Erreichte Personen'
          },
          response: {
            value: '48h',
            label: 'Durchschnittliche Reaktionszeit'
          },
          accuracy: {
            value: '94%',
            label: 'Vorhersagegenauigkeit'
          },
          organizations: {
            value: '150+',
            label: 'Partnerorganisationen'
          }
        }
      },
      ourVision: {
        title: 'Unsere Vision',
        subtitle: 'CLEAR zielt darauf ab, die Kapazität des NRC zur Vorbereitung und Reaktion auf humanitäre Krisen durch prädiktive Analysen, verbesserte Situationswahrnehmung und optimierte Informationen zu stärken—um Menschen auf der Flucht besser zu dienen.',
        items: {
          riskAlert: {
            title: 'Risiko, Warnung & Situationsbewusstsein',
            description: 'Frühere Risikoerkennung, rechtzeitige Frühwarnung und Situationsbewusstsein ermöglichen, um Notfallmaßnahmen besser informiert, schneller und wo möglich vorausschauend zu gestalten.'
          },
          communityCentered: {
            title: 'Gemeinschaftszentrierte Reaktion',
            description: 'Gemeinschaftsstimmen in jedem Schritt zentrieren, ihre Perspektiven erfassen und lokale Nothelfer mit zeitnahem Zugang zu richtigen Informationen stärken.'
          },
          aiPowered: {
            title: 'KI-gestützte Intelligenz',
            description: 'Rohdaten in umsetzbare Intelligenz verwandeln durch moderne Dateninfrastruktur, prädiktive Analysen und KI—zeitnahes Handeln und fokussierte Dienstleistung ermöglichen.'
          },
          collaboration: {
            title: 'Silos brechen, zusammen bauen',
            description: 'Bemühungen konsolidieren, organisatorische Silos aufbrechen und diverse Stakeholder um ein gemeinsames Ziel vereinen: KI in den Dienst der Menschheit zu stellen.'
          }
        },
        responsible: {
          title: 'Verantwortungsvoller KI-Einsatz',
          description: 'KI und neue Technologien sind Werkzeuge, keine Wundermittel. Wir bleiben dem verantwortungsvollen KI-Einsatz verpflichtet, wahren die Übereinstimmung mit humanitären Prinzipien, gewährleisten kontinuierliche menschliche Aufsicht und bleiben bescheiden über die Grenzen der Technologie.'
        }
      },
      vision: {
        title: 'Wie CLEAR diese Herausforderungen angeht',
        subtitle: 'CLEAR verwandelt fragmentierte Signale in einheitliche, handlungsorientierte Intelligenz—damit Feldteams schneller und effektiver reagieren können, um Leben zu retten.',
        items: {
          hoursMatter: {
            title: 'Handlungsorientierte Intelligenz',
            description: 'Verwandelt isolierte Frühwarnsignale in operative Auslöser. Feldentscheidungsträger erhalten integrierte, kontextualisierte Intelligenz—keine Rohdaten—die sofortiges Handeln ermöglicht, wenn Krisen entstehen.'
          },
          dataDriven: {
            title: 'Einheitliche Kommandozentrale',
            description: 'Konsolidiert 15+ unverbundene Systeme zu einer einzigen Echtzeit-Plattform. Eliminiert Stunden manueller Datenkorrelation und enthüllt kritische Muster sofort durch KI-gestützte Synthese.'
          },
          fieldCentered: {
            title: 'Feldangepasste Schnittstelle',
            description: 'Speziell entwickelt für humanitäre Helfer in Umgebungen mit geringer Konnektivität und hohem Stress. Intuitive Werkzeuge für komplexe Multi-Risiko-Krisen—ersetzt Ad-hoc-Koordinierung durch systematische Reaktion.'
          },
          rapidDeployment: {
            title: 'Null-Latenz-Reaktion',
            description: 'Verarbeitet und kommuniziert Intelligenz in Echtzeit. Von Erkennung bis Einsatz in Stunden statt Tagen—hält mit schnelllebigen Krisen und Kaskadeneffekten durch automatisierte Workflows Schritt.'
          }
        },
        comparison: {
          title: 'Schnellere, intelligentere humanitäre Reaktion',
          subtitle: 'CLEAR verhindert keine Krisen, reduziert aber dramatisch die Reaktionszeit und beseitigt Rätselraten bei humanitären Entscheidungen, um lebensrettende Unterstützung schneller zu denen zu bringen, die sie brauchen.',
          traditional: {
            title: 'Traditioneller Ansatz',
            steps: {
              step1: 'Krise tritt auf',
              step2: 'Auf Berichte warten',
              step3: 'Manuelle Bewertung',
              step4: 'Langsame Entscheidung',
              step5: 'Verzögerte Reaktion'
            },
            timing: {
              day1: 'Tag 1',
              day2: 'Tag 2-3',
              day3: 'Tag 4-7',
              day4: 'Tag 8-10',
              day5: 'Tag 11+'
            },
            responseTime: 'Reaktionszeit: Bis zu 4 Wochen'
          },
          withClear: {
            title: 'Mit CLEAR',
            steps: {
              step1: 'Auto-Erkennung',
              step2: 'KI-Analyse',
              step3: 'Intelligente Bewertung',
              step4: 'Datengesteuerte Entscheidung',
              step5: 'Schnelle Reaktion'
            },
            timing: {
              hour1: 'Stunde 1',
              hour2: 'Stunde 2',
              hour3: 'Stunde 4',
              hour4: 'Stunde 6',
              hour5: 'Stunde 8'
            },
            responseTime: 'Reaktionszeit: 72 Stunden'
          },
          improvements: {
            faster: 'Schnellere Reaktion',
            lessGuesswork: 'Weniger Rätselraten',
            moreData: 'Mehr Datenpunkte'
          }
        }
      },
      cta: {
        title: 'Seien Sie unter den Ersten, wenn CLEAR Open Source wird',
        subtitle: 'Schließen Sie sich humanitären Organisationen an, die sich auf die Open-Source-Veröffentlichung dieser revolutionären Krisenreaktionsplattform vorbereiten',
        button: 'Bei Start benachrichtigen'
      },
      humanitarianChallenge: {
        title: 'Die humanitäre Herausforderung',
        subtitle: 'Aktuelle humanitäre Reaktionssysteme stehen vor vier kritischen Einschränkungen, die CLEAR direkt anspricht',
        challenges: {
          intelligenceGap: {
            title: 'Die Lücke zwischen Intelligenz und Handlung',
            description: 'Bestehende Frühwarnsysteme bieten isolierte Einblicke (Konflikt, Klima, Vertreibung), produzieren aber keine operativen Auslöser. Entscheidungsträger im Feld benötigen integrierte, kontextualisierte Intelligenz, keine Rohsignale.'
          },
          fragmentation: {
            title: 'Systemfragmentierung',
            description: 'Feldteams müssen mehrere, nicht interoperable Systeme navigieren, die nicht für komplexe Multi-Risiko-Krisen konzipiert sind. Sie verlassen sich auf veraltete Berichte, Ad-hoc-Koordinierung oder Instinkt. Echtzeitentscheidungen erfordern einheitliche, feldangepasste Werkzeuge.'
          },
          latency: {
            title: 'Latenz bei Erkennung und Kommunikation',
            description: 'Selbst die besten Erkenntnisse kommen zu spät. Verzögerungen bei Verarbeitung, Kommunikation und Koordinierung bedeuten, dass Reaktionen hinter schnelllebigen Krisen zurückbleiben, besonders bei solchen mit Kaskadeneffekten.'
          },
          principledAI: {
            title: 'Die schwierige Frage der prinzipiengeleiteten KI-Nutzung',
            description: 'Während KI enormes Potenzial für humanitäre Maßnahmen bietet, müssen wir komplexe ethische Überlegungen zu Voreingenommenheit, Rechenschaftspflicht und menschlicher Aufsicht navigieren, um sicherzustellen, dass Technologie humanitären Prinzipien dient.'
          }
        }
      },
      earlyAccess: {
        title: 'Seien Sie unter den Ersten, wenn CLEAR Open Source wird',
        subtitle: 'CLEAR wird als Open-Source-Software veröffentlicht. Schließen Sie sich humanitären Organisationen an, die sich auf diese revolutionäre Frühwarn- und Reaktionsplattform vorbereiten.',
        form: {
          title: 'Bei Start benachrichtigen',
          subtitle: 'Werden Sie benachrichtigt, wenn CLEAR als Open Source verfügbar wird',
          email: {
            label: 'E-Mail-Adresse',
            placeholder: 'ihre.email@organisation.de'
          },
          organization: {
            label: 'Organisation',
            placeholder: 'UN-Agentur, NGO, Regierung...'
          },
          role: {
            label: 'Rolle',
            placeholder: 'Wählen Sie Ihre Rolle',
            options: {
              countryDirector: 'Landesdirektor',
              programManager: 'Programmmanager',
              fieldCoordinator: 'Feldkoordinator',
              dataAnalyst: 'Datenanalyst',
              emergencyCoordinator: 'Notfallkoordinator',
              researcher: 'Forscher',
              other: 'Sonstiges'
            }
          },
          submit: 'Bei Start benachrichtigen',
          submitting: 'Anmelden...',
          privacy: 'Wir kontaktieren Sie nur über den CLEAR Open-Source-Start. Kein Spam, jederzeit abmelden.',
          success: {
            title: 'Sie sind auf der Liste!',
            message: 'Vielen Dank! Sie werden zu den Ersten gehören, die erfahren, wann CLEAR Open Source wird.'
          },
          error: 'Etwas ist schief gelaufen. Bitte versuchen Sie es erneut.'
        }
      },
      navigation: {
        login: 'Anmelden',
        signup: 'Registrieren',
        language: 'Sprache'
      },
      footer: {
        description: 'Crisis Learning, Early-warning, Anticipation, and Response Plattform für humanitäre Operationen.',
        status: 'In Entwicklung',
        organization: 'Norwegischer Flüchtlingsrat',
        contact: {
          title: 'Kontakt',
          switchboard: 'Zentrale (09-15 MEZ)',
          media: 'Medien-Hotline'
        },
        donor: {
          title: 'Spender-Support',
          hours: '(10.00-12.00/ 12.30-14.00 MEZ)',
          phone: 'Telefon'
        },
        impact: 'Über 90 Prozent unserer globalen Einnahmen fließen direkt in die Erfüllung unseres Zwecks.',
        links: {
          privacy: 'Datenschutzrichtlinie',
          cookies: 'Über Cookies',
          nrc: 'Norwegischer Flüchtlingsrat'
        }
      },
      signup: {
        title: 'Beteiligen Sie sich am CLEAR-Projekt',
        subtitle: 'Werden Sie benachrichtigt, wenn CLEAR als Open Source verfügbar wird',
        placeholder: 'Geben Sie Ihre E-Mail-Adresse ein',
        button: 'Updates erhalten',
        submitting: 'Wird gesendet...',
        success: {
          title: 'Danke!',
          message: 'Sie werden benachrichtigt, wenn CLEAR startet.'
        },
        error: 'Etwas ist schief gelaufen. Bitte versuchen Sie es erneut.'
      }
    }
  },
  fr: {
    landing: {
      hero: {
        title: 'Réponse aux crises, clarifiée',
        subtitle: 'CLEAR donne aux équipes de terrain les capacités de détection de crise alimentées par l\'IA et de réponse proactive',
        description: 'Crisis Learning, Early-warning, Anticipation, and Response - Un écosystème complet pour le support de décision humanitaire',
        badge: 'Alerte précoce • Action précoce',
        cta: {
          primary: 'Être notifié au lancement',
          secondary: 'Voir la démo',
          earlyAccess: 'Obtenir l\'accès anticipé',
          learnMore: 'En savoir plus'
        }
      },
      capabilities: {
        title: 'Quatre composants intégrés',
        subtitle: 'Construits sur une fondation de données unifiée qui permet une réponse précoce pour sauver des vies',
        foundation: {
          title: 'EWEA Data Mesh',
          description: 'Couche de données fondamentale fournissant une analyse prédictive exploitant des modèles ML entraînés sur plus de 10 ans de données de crise du NRC pour prévoir les mouvements de population, les besoins en ressources et les modèles d\'évolution des crises'
        },
        components: {
          detection: {
            title: 'Moteur de détection et d\'alerte de crise',
            description: 'Surveillance en temps réel des images satellites, des modèles météorologiques, des médias sociaux, des flux d\'actualités et des rapports de terrain. L\'apprentissage automatique formé sur une décennie de données de crise identifie les modèles à haut risque, fournissant des alertes intelligentes aux décideurs via le web, l\'e-mail ou l\'application mobile en fonction de la gravité et de l\'impact géographique.'
          },
          situationAnalysis: {
            title: 'Générateur d\'analyse de situation',
            description: 'Synthèse automatisée de centaines de sources de données en intelligence exploitable. À l\'aide de grands modèles linguistiques, l\'IA traite l\'information dans plusieurs langues, identifiant les tendances et les modèles et générant des rapports de situation complets dans les heures suivant un événement.'
          },
          fieldInterface: {
            title: 'Console de réponse sur le terrain',
            description: 'Tableau de bord mobile-first offrant une intelligence opérationnelle directement aux équipes de terrain. Une interface en langage naturel permet aux utilisateurs de poser des questions aux données et de recevoir des réponses instantanées et contextuelles, même dans des environnements à faible bande passante.'
          },
          cashDistribution: {
            title: 'Moteur de distribution rapide d\'argent liquide',
            description: 'Modélisation prédictive de la vulnérabilité qui analyse la composition des ménages, la perte d\'actifs, le statut de déplacement et les risques de protection pour permettre un ciblage plus rapide et plus précis de l\'aide en espèces d\'urgence, y compris des mécanismes de validation communautaire.'
          }
        }
      },
      demo: {
        title: 'Voir CLEAR en action',
        subtitle: 'Démonstration interactive des capacités réelles du système',
        dashboard: 'Tableau de bord des crises',
        crisisView: 'Interface de réponse aux crises',
        stats: 'Métriques d\'impact en direct'
      },
      stats: {
        title: 'Impact humanitaire mondial',
        metrics: {
          crises: {
            value: '2,4M',
            label: 'Personnes atteintes'
          },
          response: {
            value: '48h',
            label: 'Temps de réponse moyen'
          },
          accuracy: {
            value: '94%',
            label: 'Précision des prédictions'
          },
          organizations: {
            value: '150+',
            label: 'Organisations partenaires'
          }
        }
      },
      ourVision: {
        title: 'Notre vision',
        subtitle: 'CLEAR vise à renforcer la capacité du NRC à se préparer et à répondre aux crises humanitaires grâce à l\'analyse prédictive, à une conscience situationnelle améliorée et à des informations rationalisées—pour mieux servir les personnes en déplacement.',
        items: {
          riskAlert: {
            title: 'Risque, alerte et conscience situationnelle',
            description: 'Permettre une détection précoce des risques, des alertes précoces opportunes et une conscience situationnelle pour rendre la réponse d\'urgence mieux informée, plus rapide et, dans la mesure du possible, anticipative.'
          },
          communityCentered: {
            title: 'Réponse centrée sur la communauté',
            description: 'Centrer les voix de la communauté à chaque étape, capturer leurs perspectives et donner aux intervenants d\'urgence locaux un accès opportun aux bonnes informations.'
          },
          aiPowered: {
            title: 'Intelligence alimentée par l\'IA',
            description: 'Transformer les données brutes en intelligence exploitable grâce à une infrastructure de données moderne, à l\'analyse prédictive et à l\'IA—permettant une action opportune et une prestation de services ciblée.'
          },
          collaboration: {
            title: 'Briser les silos, construire ensemble',
            description: 'Consolider les efforts, briser les silos organisationnels et rassembler diverses parties prenantes autour d\'un objectif commun : mettre l\'IA au service de l\'humanité.'
          }
        },
        responsible: {
          title: 'Utilisation responsable de l\'IA',
          description: 'L\'IA et les technologies émergentes sont des outils, pas des solutions miracles. Nous restons engagés dans une utilisation responsable de l\'IA, en maintenant l\'alignement avec les principes humanitaires, en assurant une supervision humaine continue et en restant humbles face aux limites de la technologie.'
        }
      },
      vision: {
        title: 'Comment CLEAR relève ces défis',
        subtitle: 'CLEAR transforme les signaux fragmentés en intelligence unifiée et exploitable—permettant aux équipes de terrain de répondre plus rapidement et plus efficacement pour sauver des vies.',
        items: {
          hoursMatter: {
            title: 'Intelligence exploitable',
            description: 'Transforme les signaux d\'alerte précoce cloisonnés en déclencheurs opérationnels. Les décideurs de terrain reçoivent une intelligence intégrée et contextualisée—pas de données brutes—permettant une action immédiate lors de l\'émergence des crises.'
          },
          dataDriven: {
            title: 'Centre de commandement unifié',
            description: 'Consolide 15+ systèmes déconnectés en une seule plateforme temps réel. Élimine des heures de corrélation manuelle de données, révélant instantanément des modèles critiques grâce à la synthèse alimentée par l\'IA.'
          },
          fieldCentered: {
            title: 'Interface adaptée au terrain',
            description: 'Conçu spécifiquement pour les travailleurs humanitaires dans des environnements à faible connectivité et à fort stress. Outils intuitifs conçus pour les crises multi-risques complexes—remplaçant la coordination ad hoc par une réponse systématique.'
          },
          rapidDeployment: {
            title: 'Réponse à latence zéro',
            description: 'Traite et communique l\'intelligence en temps réel. De la détection au déploiement en heures au lieu de jours—suivant le rythme des crises à évolution rapide et des effets en cascade grâce aux flux de travail automatisés.'
          }
        },
        comparison: {
          title: 'Réponse humanitaire plus rapide et plus intelligente',
          subtitle: 'CLEAR ne prévient pas les crises, mais réduit considérablement le temps de réponse et élimine les conjectures de la prise de décision humanitaire, apportant un soutien vital plus rapidement à ceux qui en ont besoin.',
          traditional: {
            title: 'Approche traditionnelle',
            steps: {
              step1: 'Crise survient',
              step2: 'Attendre les rapports',
              step3: 'Évaluation manuelle',
              step4: 'Décision lente',
              step5: 'Réponse retardée'
            },
            timing: {
              day1: 'Jour 1',
              day2: 'Jour 2-3',
              day3: 'Jour 4-7',
              day4: 'Jour 8-10',
              day5: 'Jour 11+'
            },
            responseTime: 'Temps de réponse : Jusqu\'à 4 semaines'
          },
          withClear: {
            title: 'Avec CLEAR',
            steps: {
              step1: 'Détection automatique',
              step2: 'Analyse IA',
              step3: 'Évaluation intelligente',
              step4: 'Décision basée sur les données',
              step5: 'Réponse rapide'
            },
            timing: {
              hour1: 'Heure 1',
              hour2: 'Heure 2',
              hour3: 'Heure 4',
              hour4: 'Heure 6',
              hour5: 'Heure 8'
            },
            responseTime: 'Temps de réponse : 72 heures'
          },
          improvements: {
            faster: 'Réponse plus rapide',
            lessGuesswork: 'Moins de conjectures',
            moreData: 'Plus de points de données'
          }
        }
      },
      earlyAccess: {
        title: 'Soyez parmi les premiers quand CLEAR devient open source',
        subtitle: 'CLEAR sera publié comme logiciel open source. Rejoignez les organisations humanitaires qui se préparent à cette plateforme révolutionnaire d\'alerte précoce et de réponse.',
        form: {
          title: 'Être notifié au lancement',
          subtitle: 'Soyez notifié quand CLEAR devient disponible comme open source',
          email: {
            label: 'Adresse e-mail',
            placeholder: 'votre.email@organisation.fr'
          },
          organization: {
            label: 'Organisation',
            placeholder: 'Agence ONU, ONG, Gouvernement...'
          },
          role: {
            label: 'Rôle',
            placeholder: 'Sélectionnez votre rôle',
            options: {
              countryDirector: 'Directeur de pays',
              programManager: 'Chef de programme',
              fieldCoordinator: 'Coordinateur de terrain',
              dataAnalyst: 'Analyste de données',
              emergencyCoordinator: 'Coordinateur d\'urgence',
              researcher: 'Chercheur',
              other: 'Autre'
            }
          },
          submit: 'Être notifié au lancement',
          submitting: 'Inscription...',
          privacy: 'Nous vous contacterons uniquement concernant le lancement open source de CLEAR. Pas de spam, désabonnement à tout moment.',
          success: {
            title: 'Vous êtes sur la liste !',
            message: 'Merci ! Vous serez parmi les premiers à savoir quand CLEAR devient open source.'
          },
          error: 'Quelque chose s\'est mal passé. Veuillez réessayer.'
        }
      },
      cta: {
        title: 'Soyez parmi les premiers quand CLEAR devient open source',
        subtitle: 'Rejoignez les organisations humanitaires qui se préparent à la publication open source de cette plateforme révolutionnaire de réponse aux crises',
        button: 'Être notifié au lancement'
      },
      humanitarianChallenge: {
        title: 'Le défi humanitaire',
        subtitle: 'Les systèmes de réponse humanitaire actuels font face à quatre limitations critiques que CLEAR aborde directement',
        challenges: {
          intelligenceGap: {
            title: 'L\'écart entre intelligence et action',
            description: 'Les outils d\'alerte précoce existants offrent des aperçus cloisonnés (conflit, climat, déplacement) mais ne produisent pas de déclencheurs opérationnels. Les décideurs sur le terrain ont besoin d\'intelligence intégrée et contextualisée, pas de signaux bruts.'
          },
          fragmentation: {
            title: 'Fragmentation des systèmes',
            description: 'Les équipes de terrain doivent naviguer entre plusieurs systèmes non interopérables non conçus pour les crises multi-risques complexes. Ils s\'appuient sur des rapports obsolètes, une coordination ad hoc ou l\'instinct. Les décisions en temps réel exigent des outils unifiés et adaptés au terrain.'
          },
          latency: {
            title: 'Latence dans la détection et la communication',
            description: 'Même les meilleures informations arrivent trop tard. Les retards de traitement, de communication et de coordination signifient que les réponses sont en retard par rapport aux crises en évolution rapide, en particulier celles ayant des effets en cascade.'
          },
          principledAI: {
            title: 'La question difficile de l\'utilisation éthique de l\'IA',
            description: 'Bien que l\'IA offre un potentiel immense pour l\'action humanitaire, nous devons naviguer dans des considérations éthiques complexes concernant les biais, la responsabilité et la supervision humaine pour garantir que la technologie serve les principes humanitaires.'
          }
        }
      },
      navigation: {
        login: 'Connexion',
        signup: 'S\'inscrire',
        language: 'Langue'
      },
      footer: {
        description: 'Plateforme Crisis Learning, Early-warning, Anticipation, and Response pour les opérations humanitaires.',
        status: 'En développement',
        organization: 'Conseil norvégien pour les réfugiés',
        contact: {
          title: 'Contact',
          switchboard: 'Standard (09-15 CET)',
          media: 'Ligne directe médias'
        },
        donor: {
          title: 'Support donateurs',
          hours: '(10.00-12.00/ 12.30-14.00 CET)',
          phone: 'Téléphone'
        },
        impact: 'Plus de 90 % de nos revenus mondiaux vont directement à l\'accomplissement de notre mission.',
        links: {
          privacy: 'Politique de confidentialité',
          cookies: 'À propos des cookies',
          nrc: 'Conseil norvégien pour les réfugiés'
        }
      },
      signup: {
        title: 'Participez au projet CLEAR',
        subtitle: 'Soyez notifié quand CLEAR devient disponible comme open source',
        placeholder: 'Entrez votre adresse e-mail',
        button: 'Recevoir les mises à jour',
        submitting: 'Envoi en cours...',
        success: {
          title: 'Merci !',
          message: 'Vous serez notifié quand CLEAR sera lancé.'
        },
        error: 'Quelque chose s\'est mal passé. Veuillez réessayer.'
      }
    }
  }
};

// Initialize i18n with optimized configuration
i18n
  .use(initReactI18next)
  .init({
    resources,
    lng: 'en',
    fallbackLng: 'en',
    fallbackNS: 'landing',
    
    // Interpolation settings
    interpolation: {
      escapeValue: false
    },
    
    // React specific settings
    react: {
      useSuspense: false
    },
    
    // Debug settings (disable in production)
    debug: process.env.NODE_ENV === 'development',
    
    // Namespace settings
    ns: ['landing', 'about'],
    defaultNS: 'landing',
    
    // Key separator
    keySeparator: '.',
    
    // Optimizations
    load: 'languageOnly', // Only load exact language matches
    cleanCode: true, // Clean language codes
    initImmediate: false, // Don't wait for initialization
    
    // Missing translation handling
    saveMissing: false, // Don't save missing keys in production
    missingKeyHandler: process.env.NODE_ENV === 'development' ? 
      (lng, ns, key) => console.warn(`Missing translation: ${lng}.${ns}.${key}`) : 
      undefined
  });

export default i18n;